import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { ProcessDTO, ProcessStepDTO } from '@/types/api-types';

import IconMobileLogo from '@/assets/svg/icon-add-mobile.svg?react';
import IconCloseOut from '@/assets/svg/icon-close-out.svg?react';

import { useIsMobile } from '@/hooks/use-mobile';
import { usePrompt } from '@/hooks/usePrompt';

import { Button } from '@/components/ui/button';
import { AddStepDialog } from '@/components/AddStepDialog';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';

import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

import { USER_ROLES } from '@/constants/global-constants';
import { adminRoutes, privateRoutes } from '@/constants/route-settings';

import { AddStepsTable } from './AddStepsTable';

export function EditProcessTab() {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const isMobile = useIsMobile();

  usePrompt(hasUnsavedChanges);

  const [isLoading, setIsLoading] = useState(true);

  // Add effect to disable body scrolling
  useEffect(() => {
    // Save original styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;

    // Disable scrolling
    document.body.style.overflow = 'hidden';

    // Cleanup function to restore original styles
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const { deleteProcessStep, editProcess, bulkUpdateSteps, bulkSaveSteps } =
    useGlobalStore();

  const { indexes, steps, getProcessByUuid } = useGlobalStore();

  const visibleIndexes = useMemo(() => {
    return indexes.filter((index) => !index.is_deleted);
  }, [indexes]);

  const navigate = useNavigate();

  const [tempSteps, setTempSteps] = useState<ProcessStepDTO[]>([]);

  const [isSaving, setIsSaving] = useState(false);

  const { uuid: processId } = useParams<{ uuid: ProcessDTO['uuid'] }>();

  useEffect(() => {
    const init = async (processId: string) => {
      let process = getProcessByUuid(processId);
      if (!process) {
        process = await apiService.getProcess(processId);
      }
      setProcessItemEdit(process);
      setTempSteps(
        steps.filter(
          (step) => process!.steps.includes(step.uuid) && !step.is_deleted,
        ),
      );
      setIsLoading(false);
    };
    if (processId) {
      init(processId);
    }
  }, [processId, steps]);

  const [processItemEdit, setProcessItemEdit] = useState<ProcessDTO | null>(
    null,
  );

  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [newStepId, setNewStepId] = useState(false);
  const [deletingStepId, setDeletingStepId] = useState<string | null>(null);

  const handleAddNewStep = () => {
    if (processItemEdit) {
      setNewStepId(true);
    }
  };

  const handleAddOrEditStep = async (data: Partial<ProcessStepDTO>) => {
    if (data.uuid) {
      setTempSteps((prev) =>
        prev.map((step) =>
          step.uuid === data.uuid ? (data as ProcessStepDTO) : step,
        ),
      );
    } else {
      setTempSteps((prev) => [
        ...prev,
        {
          status_name: '',
          days_until_deadline: 0,
          user_type: USER_ROLES.EXECUTOR.value,
          comment: '',
          process_set: [],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          ...data,
          uuid: uuidv4(),
        },
      ]);
    }
    setNewStepId(false);
    setHasUnsavedChanges(true);
  };

  const onConfirmDelete = async () => {
    if (deletingStepId) {
      const step = tempSteps.find((step) => step.uuid === deletingStepId);
      if (
        step &&
        step.process_set.length &&
        step.process_set.includes(deletingStepId)
      ) {
        await deleteProcessStep(deletingStepId);
      } else {
        setTempSteps((prev) =>
          prev.filter((step) => step.uuid !== deletingStepId),
        );
      }
      setDeletingStepId(null);
      setHasUnsavedChanges(true);
    }
  };

  const handleSaveProcess = async () => {
    if (processItemEdit) {
      setIsSaving(true);
      try {
        const oldSteps = tempSteps.filter((step) =>
          processItemEdit.steps.includes(step.uuid),
        );

        const newSteps = tempSteps
          .filter(
            (step) =>
              !processItemEdit.steps.includes(step.uuid) && !step.is_deleted,
          )
          .map((step) => ({
            ...step,
            processes: [processItemEdit.uuid],
          }));

        const oldStepsUuids = await bulkUpdateSteps(oldSteps);
        let newStepsUuids: string[] = [];
        if (newSteps.length) {
          newStepsUuids = await bulkSaveSteps(newSteps);
        }

        setHasUnsavedChanges(false);
        await editProcess({
          ...processItemEdit,
          steps: [...oldStepsUuids, ...newStepsUuids],
        });
        setIsSaving(false);
        navigate(
          `${privateRoutes[USER_ROLES.ADMIN.value].settings}/${privateRoutes[USER_ROLES.ADMIN.value].processes.list}`,
        );
      } catch (error) {
        setIsSaving(false);
      }
    }
  };

  const renderIndexName = () => {
    if (processItemEdit) {
      if (processItemEdit.index) {
        const title = `${processItemEdit.name} - ${
          visibleIndexes.find((index) => index.uuid === processItemEdit.index)
            ?.description
        }`;
        return (
          <div
            className="min-w-[50px] max-w-[70vw] items-center justify-between overflow-hidden text-ellipsis whitespace-nowrap rounded-md border border-gray-200 bg-white px-5 py-3.5 text-base sm:h-[50px] sm:max-w-[500px]"
            title={title}
          >
            {title}
          </div>
        );
      }
      return (
        <div className="flex min-w-[50px] max-w-[80vw] items-center rounded-md border border-gray-200 bg-white px-5 py-3.5 text-base sm:h-[50px] sm:max-w-[500px]">
          <span>{processItemEdit.name}</span>
        </div>
      );
    }

    return '';
  };

  return (
    <>
      <ConfirmationDialog
        isOpen={!!deletingStepId}
        onClose={() => setDeletingStepId(null)}
        onConfirm={onConfirmDelete}
        title="Видалити етап?"
        cancelText="Скасувати"
        confirmText="Видалити"
      />
      <AddStepDialog
        open={newStepId}
        onClose={() => setNewStepId(false)}
        onSubmit={handleAddOrEditStep}
        editingStep={null}
      />

      <AddStepDialog
        open={!!editingStepId}
        onClose={() => setEditingStepId(null)}
        onSubmit={handleAddOrEditStep}
        editingStep={
          tempSteps.find((step) => step.uuid === editingStepId) || null
        }
      />
      <div className="flex h-[calc(100vh-150px)] flex-col overflow-hidden rounded-md bg-white shadow-sm sm:max-w-[1500px]">
        <div className="flex-shrink-0">
          <div className="flex justify-between pl-8 pr-5 pt-5">
            <div className="mb-6 flex flex-col gap-2">
              <h2 className="mt-1 text-2xl font-medium text-primary">
                Редагувати процес
              </h2>
              <p className="font-medium">
                Редагуйте етапи для обраного процесу
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={() =>
                navigate(
                  `${adminRoutes.settings}/${adminRoutes.processes.list}`,
                )
              }
              className="h-[22px] w-[22px]"
            >
              <IconCloseOut className="h-[22px] w-[22px] fill-white hover:fill-primary" />
            </Button>
          </div>
          <div className="mb-2 flex items-center justify-between pl-8 pr-6">
            {renderIndexName()}
            {isMobile ? (
              <button
                className="rounded-md text-white [&:disabled_svg]:fill-gray-400"
                onClick={handleAddNewStep}
                type="button"
              >
                <IconMobileLogo className="h-[34px] w-[34px] fill-primary" />
              </button>
            ) : (
              <Button
                variant="default"
                type="button"
                onClick={handleAddNewStep}
              >
                Додати етап
              </Button>
            )}
          </div>
        </div>

        <div className="flex-grow overflow-auto">
          <AddStepsTable
            steps={tempSteps}
            onEdit={(id) => setEditingStepId(id)}
            onDelete={(id) => setDeletingStepId(id)}
            isLoading={isLoading || isSaving}
          />
        </div>

        <div className="flex flex-shrink-0 justify-end p-6">
          <Button
            variant={!tempSteps.length ? 'default' : 'outline'}
            disabled={!tempSteps.length || isSaving}
            onClick={handleSaveProcess}
          >
            {isSaving ? 'Збереження...' : 'Зберегти'}
          </Button>
        </div>
      </div>
    </>
  );
}
